// React imports
import { useState, useMemo } from 'react';

// Other imports
import axios from 'axios';
import dayjs from 'dayjs';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import { useTheme, alpha } from '@mui/material/styles';

// Local imports
import { Pill } from './ElariomParts.js';
import { CONVERTER_TITLES } from './elariomData.js';
import { useOlderRates, restoreOlderRates, OlderRatesPanel, olderRatesSentence } from './ElariomOlderRates.js';
import { rateCharges, rateTransitText } from './rateChargeText.js';

// Review & publish for a file one of Monada's converters made into rates (a metro trucking
// sheet, an LCL tariff with CBM bands). The rates are already finished, with formulas and door
// locations a CSV can't express, so they are shown as they are: the reviewer leaves out rows,
// decides what happens to older versions, and publishes. To change how they were made (port,
// fuel surcharge, validity), convert the file again with other settings.

const CHUNK = 100;
const PAGE = 300;

function ElariomReviewRates({ org, accesstoken, drop, runSummary, run, onClose, onPublished }) {
    const theme = useTheme();
    const rates = run.rates || [];
    const [ omitted, setOmitted ] = useState(() => new Set());
    const [ filter, setFilter ] = useState('');
    const [ page, setPage ] = useState(0);
    const [ confirm, setConfirm ] = useState(false);
    const [ progress, setProgress ] = useState(null);
    const [ error, setError ] = useState(null);
    const publication = runSummary.publication?.status === 'published' ? runSummary.publication : null;
    const workspace = org.title || org.alias;

    const rows = useMemo(() => rates.map((r, i) => ({
        i, from: r.source?.text || 'Anywhere', to: r.destination?.text || 'Anywhere', product: r.product?.type || '',
        supplier: r.supplier?.organization || '', validFrom: r.offer?.validFrom || '', validUntil: r.offer?.validUntil || '',
        charges: rateCharges(r),
        transit: rateTransitText(r),
    })), [rates]);
    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return q ? rows.filter(r => `${r.from} ${r.to} ${r.product}`.toLowerCase().includes(q)) : rows;
    }, [rows, filter]);
    const toPublish = useMemo(() => rates.filter((r, i) => !omitted.has(i)), [rates, omitted]);
    const older = useOlderRates({ org, accesstoken, rates: toPublish, ownIds: runSummary.publication?.rateIds });

    const publish = async () => {
        setError(null);
        try {
            const source = drop.files.find(f => f.name === runSummary.fileName)?.url || null;
            const batch = toPublish.map(r => ({ ...r, attributes: { ...(r.attributes || {}), ...(source ? { source } : {}) } }));
            const rateIds = [];
            const failedOnSave = [];
            for (let i = 0; i < batch.length; i += CHUNK) {
                setProgress({ label: `Publishing rates ${i + 1}–${Math.min(i + CHUNK, batch.length)} of ${batch.length}…`, value: Math.round((i / batch.length) * 85) });
                const { data } = await axios.post(`/api/rates/bulk?accesstoken=${accesstoken}&organizationId=${org.id}`, { rates: batch.slice(i, i + CHUNK) });
                rateIds.push(...(data.successful || []).map(s => s.rate?.id || s.id).filter(Boolean));
                failedOnSave.push(...(data.failed || []));
            }
            let replacedRecord = null;
            if (older.replaced.length) {
                setProgress({ label: 'Handling the older versions of these rates…', value: 90 });
                replacedRecord = await older.apply();
            }
            setProgress({ label: 'Recording what was published…', value: 95 });
            const { data: record } = await axios.put(`/api/admin/elariom/${org.id}/drops/${drop.id}/runs/${runSummary.id}/publication?accesstoken=${accesstoken}`, {
                status: 'published', formId: null, formTitle: null, rateIds, corrections: [],
                omittedRows: [...omitted].map(i => i + 1),
                failedRows: failedOnSave.map(f => `save: ${f.error || 'failed'}`),
                replaced: replacedRecord,
            });
            setProgress(null);
            setConfirm(false);
            onPublished(record);
        } catch (e) {
            setProgress(null);
            setError(e.response?.data?.detail || e.message);
        }
    };

    const unpublish = async () => {
        setError(null);
        try {
            const ids = publication.rateIds || [];
            for (let i = 0; i < ids.length; i += CHUNK) {
                setProgress({ label: `Removing rates ${i + 1}–${Math.min(i + CHUNK, ids.length)} of ${ids.length}…`, value: Math.round((i / Math.max(ids.length, 1)) * 90) });
                await axios.post(`/api/rates/admin-delete?accesstoken=${accesstoken}&organizationId=${org.id}`, { rates: ids.slice(i, i + CHUNK) });
            }
            if (publication.replaced) {
                setProgress({ label: 'Putting the older rates back as they were…', value: 95 });
                await restoreOlderRates({ org, accesstoken, record: publication.replaced });
            }
            const { data: record } = await axios.put(`/api/admin/elariom/${org.id}/drops/${drop.id}/runs/${runSummary.id}/publication?accesstoken=${accesstoken}`, { status: 'unpublished' });
            setProgress(null);
            setConfirm(false);
            onPublished(record);
        } catch (e) {
            setProgress(null);
            setError(e.response?.data?.detail || e.message);
        }
    };

    const shown = visible.slice(page * PAGE, (page + 1) * PAGE);
    const pages = Math.ceil(visible.length / PAGE);
    const th = { position: 'sticky', top: 0, zIndex: 1, background: theme.palette.panel1, fontSize: '11px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', padding: '6px 8px', borderBottom: `1px solid ${theme.palette.border1}`, color: theme.palette.text.secondary };
    const td = { fontSize: '12px', padding: '3px 8px', borderBottom: `1px solid ${alpha(theme.palette.text.secondary, 0.12)}`, whiteSpace: 'nowrap', verticalAlign: 'top' };

    return (
        <Dialog open onClose={() => !progress && onClose()} fullScreen>
            <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Review rates · {run.supplier?.name || 'supplier'}</Typography>
                    <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                        {runSummary.fileName} · made by the {CONVERTER_TITLES[run.converter] || 'converter'}, no agent · publishes to <b>{workspace}</b>
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Pill color={theme.palette.text.secondary}>{rates.length} rates</Pill>
                    <Pill color={theme.palette.primary.main}>{toPublish.length} to publish</Pill>
                    {omitted.size > 0 && <Pill color={theme.palette.text.secondary}>{omitted.size} left out</Pill>}
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {publication && (
                    <Alert severity='success' sx={{ fontSize: '13px' }}>
                        Published {publication.rateCount} rates to {workspace} on {dayjs(publication.at).format('MMM D, HH:mm')} by {publication.by}.
                        {publication.replaced?.count ? ` ${publication.replaced.count} older version${publication.replaced.count === 1 ? ' was' : 's were'} ${publication.replaced.action === 'end' ? 'ended the day before the new rates start' : publication.replaced.action === 'delete' ? 'deleted' : 'kept as they were'}.` : ''}
                    </Alert>
                )}
                {runSummary.publication?.status === 'unpublished' && (
                    <Alert severity='info' sx={{ fontSize: '13px' }}>
                        Unpublished on {dayjs(runSummary.publication.unpublishedAt).format('MMM D, HH:mm')}: its rates were removed from {workspace}, and any older rates they replaced were put back.
                    </Alert>
                )}
                {error && <Alert severity='error' sx={{ fontSize: '13px' }}>{error}</Alert>}
                {(run.notes || []).map((n, i) => <Typography key={i} sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>{n}</Typography>)}
                {!!(run.errors || []).length && (
                    <Alert severity='warning' sx={{ fontSize: '13px' }}>
                        {run.errors.length} row{run.errors.length === 1 ? ' was' : 's were'} skipped by the converter: {run.errors.slice(0, 5).map(e => `row ${e.row + 1} (${e.reason})`).join(', ')}{run.errors.length > 5 ? ', …' : ''}.
                    </Alert>
                )}
                {!publication && <OlderRatesPanel older={older} workspace={workspace} />}
                {older.loading && !publication && <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>Looking for older versions of these rates in {workspace}…</Typography>}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <TextField id='elariom-review-filter' size='small' placeholder='Find a place or product' value={filter} onChange={e => { setFilter(e.target.value); setPage(0); }} sx={{ width: '280px' }} />
                    {!publication && (
                        <>
                            <Button size='small' sx={{ textTransform: 'none' }} onClick={() => setOmitted(prev => { const next = new Set(prev); visible.forEach(r => next.delete(r.i)); return next; })}>Tick all shown</Button>
                            <Button size='small' sx={{ textTransform: 'none' }} onClick={() => setOmitted(prev => { const next = new Set(prev); visible.forEach(r => next.add(r.i)); return next; })}>Untick all shown</Button>
                        </>
                    )}
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        {visible.length === rows.length ? `${rows.length} rates` : `${visible.length} of ${rows.length} rates match`}{pages > 1 ? ` · page ${page + 1} of ${pages}` : ''}
                    </Typography>
                    {pages > 1 && (
                        <>
                            <Button size='small' disabled={page === 0} onClick={() => setPage(p => p - 1)} sx={{ textTransform: 'none' }}>Previous</Button>
                            <Button size='small' disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)} sx={{ textTransform: 'none' }}>Next</Button>
                        </>
                    )}
                </Box>
                <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                    To skip a rate, untick its row. To change how the rates were made (the port, fuel surcharge or validity), close this and convert the file again with other settings.
                </Typography>
                <Box sx={{ flexGrow: 1, overflow: 'auto', border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', background: theme.palette.panel1 }}>
                    <Box component='table' sx={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
                        <thead>
                            <tr>
                                <Box component='th' sx={{ ...th, width: '36px' }} />
                                {['From', 'To', 'Product', 'Supplier', 'Valid', 'Transit', 'Charges'].map(h => <Box component='th' key={h} sx={th}>{h}</Box>)}
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map(r => (
                                <Box component='tr' key={r.i} sx={{ opacity: omitted.has(r.i) ? 0.45 : 1 }}>
                                    <Box component='td' sx={{ ...td, padding: 0 }}>
                                        <Checkbox size='small' disabled={!!publication} checked={!omitted.has(r.i)} inputProps={{ 'aria-label': `Publish ${r.from} to ${r.to}, ${r.product}` }}
                                            onChange={() => setOmitted(prev => { const next = new Set(prev); next.has(r.i) ? next.delete(r.i) : next.add(r.i); return next; })} />
                                    </Box>
                                    <Box component='td' sx={td}>{r.from}</Box>
                                    <Box component='td' sx={td}>{r.to}</Box>
                                    <Box component='td' sx={td}>{r.product}</Box>
                                    <Box component='td' sx={td}>{r.supplier}</Box>
                                    <Box component='td' sx={td}>{r.validFrom || '…'} → {r.validUntil || 'no end'}</Box>
                                    <Box component='td' sx={td}>{r.transit}</Box>
                                    <Box component='td' sx={{ ...td, whiteSpace: 'normal', minWidth: '320px' }}>
                                        {r.charges.map((c, j) => <div key={j}><span style={{ color: theme.palette.text.secondary }}>{c.title}:</span> {c.text}</div>)}
                                    </Box>
                                </Box>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '12px 24px', gap: '8px' }}>
                <Button onClick={onClose} disabled={!!progress} sx={{ textTransform: 'none' }}>Close</Button>
                {publication
                    ? <Button variant='outlined' color='error' onClick={() => setConfirm(true)} disabled={!!progress} sx={{ textTransform: 'none' }}>Unpublish {publication.rateCount} rates</Button>
                    : <Button variant='contained' color='monada-button' onClick={() => setConfirm(true)} disabled={!toPublish.length || older.loading || !!progress} sx={{ textTransform: 'none' }}>
                        Publish {toPublish.length} rates to {workspace}
                    </Button>}
            </DialogActions>

            <Dialog open={confirm} onClose={() => !progress && setConfirm(false)} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontSize: '17px' }}>{publication ? `Unpublish from ${workspace}?` : `Publish to ${workspace}?`}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {publication ? (
                        <Typography sx={{ fontSize: '14px' }}>
                            This deletes the {publication.rateCount} rates published from this file. Quotes already priced with them keep their prices.
                            {publication.replaced && ['end', 'delete'].includes(publication.replaced.action) ? ` The ${publication.replaced.count} older rates this file replaced are put back as they were.` : ''}
                        </Typography>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '14px' }}>This creates {toPublish.length} rates in {workspace}. Rate search and quoting use them right away.</Typography>
                            <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                                {olderRatesSentence(older)}You can unpublish this file later from the same place, which also puts any older rates back.
                            </Typography>
                        </>
                    )}
                    {progress && (
                        <Box>
                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, marginBottom: '4px' }}>{progress.label}</Typography>
                            <LinearProgress variant='determinate' value={progress.value} />
                        </Box>
                    )}
                    {error && <Alert severity='error' sx={{ fontSize: '13px' }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(false)} disabled={!!progress} sx={{ textTransform: 'none' }}>Cancel</Button>
                    {publication
                        ? <Button variant='contained' color='error' onClick={unpublish} disabled={!!progress} sx={{ textTransform: 'none' }}>Unpublish</Button>
                        : <Button variant='contained' color='monada-button' onClick={publish} disabled={!!progress} sx={{ textTransform: 'none' }}>Publish</Button>}
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}

export default ElariomReviewRates;
